import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { io as ioClient } from "socket.io-client";
import { CheckCheck, Filter, MessageSquare, ShieldAlert, UserRound, Zap, Mail, Bell, ChevronDown, X } from "lucide-react";
import api, { API_ORIGIN } from "../api";
import UserLayout from "../components/Userlayout";

const FILTERS = ["All", "Unread", "High Priority"];

function timeAgo(date) {
  const timestamp = new Date(date).getTime();
  if (!Number.isFinite(timestamp)) return "Recently";
  const minutes = Math.max(1, Math.floor((Date.now() - timestamp) / 60000));
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function notificationMeta(type) {
  if (type === "quote_declined") return { Icon: ShieldAlert, background: "#ffe0dc", foreground: "#db2731" };
  if (type === "quote_sent" || type === "quote_approved") return { Icon: Zap, background: "#e6f6ea", foreground: "#24954b" };
  if (type === "comment_new") return { Icon: MessageSquare, background: "#dce9ff", foreground: "#293b8c" };
  if (type === "system" || type === "summary") return { Icon: Mail, background: "#e8eaff", foreground: "#475179" };
  return { Icon: UserRound, background: "#dce9ff", foreground: "#293b8c" };
}

export default function UserNotifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [filterOpen, setFilterOpen] = useState(false);
  const [hiddenIds, setHiddenIds] = useState([]);
  const filterRef = useRef(null);
  const userId = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "null")?.id; }
    catch { return undefined; }
  })();

  useEffect(() => {
    let mounted = true;
    api.get("/notifications")
      .then(({ data }) => { if (mounted) setItems(Array.isArray(data) ? data : []); })
      .catch(() => { if (mounted) setItems([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!userId) return undefined;
    const socket = ioClient(API_ORIGIN, {
      transports: ["websocket", "polling"],
      auth: { token: localStorage.getItem("token") },
    });
    socket.emit("join", `user:${userId}`);
    socket.on("notification:new", (item) => {
      setItems((current) => item?._id && current.some((n) => n._id === item._id)
        ? current : [item, ...current]);
    });
    return () => socket.disconnect();
  }, [userId]);

  useEffect(() => {
    const onOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) setFilterOpen(false);
    };
    const onEscape = (event) => { if (event.key === "Escape") setFilterOpen(false); };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, []);

  async function markRead(id) {
    setItems((current) => current.map((n) => n._id === id ? { ...n, read: true } : n));
    try { await api.patch(`/notifications/${id}/read`); }
    catch (error) { console.error("Failed to mark notification read", error); }
  }

  async function markAllRead() {
    setItems((current) => current.map((n) => ({ ...n, read: true })));
    try { await api.patch("/notifications/read-all"); }
    catch (error) { console.error("Failed to mark all notifications read", error); }
  }

  const visible = useMemo(() => items.filter((n) => {
    if (hiddenIds.includes(n._id)) return false;
    if (filter === "Unread" && n.read) return false;
    if (filter === "High Priority" && String(n.priority).toLowerCase() !== "high") return false;
    return true;
  }), [items, filter, hiddenIds]);

  const unread = items.filter((n) => !n.read && !hiddenIds.includes(n._id)).length;

  function openDetails(n) {
    if (!n.read) markRead(n._id);
    if (n.link) navigate(n.link);
  }

  return (
    <UserLayout>
      <main className="agp-notifications-page">
        <div className="agp-page-wrap agp-notifications-wrap">
          <div className="agp-notifications-heading">
            <h1>Notifications</h1>
            <p>Showing latest notifications for your account.</p>
          </div>

          <div className="agp-notifications-toolbar">
            <button type="button" className="agp-notifications-mark-all" onClick={markAllRead} disabled={unread === 0}>
              <CheckCheck size={16} /> Mark all as read
            </button>
            <div className="agp-notifications-filter-holder" ref={filterRef}>
              <button type="button" className="agp-notifications-filter-btn" aria-expanded={filterOpen} onClick={() => setFilterOpen((v) => !v)}>
                <Filter size={15} /> {filter === "All" ? "Filter" : filter} <ChevronDown size={13} />
              </button>
              {filterOpen && (
                <div className="agp-notifications-filter-menu">
                  {FILTERS.map((name) => (
                    <button key={name} type="button" className={filter === name ? "selected" : ""} onClick={() => { setFilter(name); setFilterOpen(false); }}>{name}</button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="agp-notifications-list" aria-live="polite">
            {loading ? (
              <div className="agp-notifications-empty">Loading notifications…</div>
            ) : visible.length === 0 ? (
              <div className="agp-notifications-empty"><Bell size={23}/><span>No notifications found.</span></div>
            ) : visible.map((n) => {
              const { Icon, background, foreground } = notificationMeta(n.type);
              const highPriority = String(n.priority).toLowerCase() === "high";
              return (
                <article key={n._id} className={`agp-notification-card ${!n.read ? "is-unread" : ""}`}>
                  <div className="agp-notification-icon" style={{ backgroundColor: background, color: foreground }}>
                    <Icon size={21} strokeWidth={2.3} />
                    {!n.read && <span className="agp-notification-unread-dot" />}
                  </div>
                  <div className="agp-notification-body">
                    <h2>{n.title || "Notification update"}</h2>
                    <time dateTime={n.createdAt || undefined}>{timeAgo(n.createdAt)}</time>
                    <p>{n.message || "Open to view more details."}</p>
                    {highPriority && <span className="agp-notification-priority">High Priority</span>}
                    <div className="agp-notification-actions">
                      {n.link && <button type="button" className="agp-notification-detail" onClick={() => openDetails(n)}>View Detail</button>}
                      {!n.read && <button type="button" onClick={() => markRead(n._id)}>Mark as Read</button>}
                      <button type="button" onClick={() => setHiddenIds((current) => [...current, n._id])}>Dismiss</button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </main>
      <style>{`
        .agp-notifications-page { min-height: calc(100vh - 92px); padding: 32px 0 56px; background: #f7f7f9; }
        .agp-notifications-wrap { max-width: 960px; }
        .agp-notifications-heading h1 { margin: 0; color: #202534; font-weight: 700; font-size: 26px; }
        .agp-notifications-heading p { color: #555e70; font-size: 14px; margin: 6px 0 0; }
        .agp-notifications-toolbar { display: flex; align-items: center; gap: 12px; margin: 20px 0 16px; flex-wrap: wrap; }
        .agp-notifications-toolbar button { font-size: 13px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-height: 38px; border-radius: 7px; padding: 0 14px; }
        .agp-notifications-mark-all { border: 1px solid #a8aec1; background: #fff; color: #25304b; }
        .agp-notifications-mark-all:disabled { opacity: .55; cursor: not-allowed; }
        .agp-notifications-filter-holder { position: relative; }
        .agp-notifications-filter-btn { border: 1px solid #26266c; background: #292771; color: white; }
        .agp-notifications-filter-menu { position: absolute; z-index: 30; top: calc(100% + 7px); left: 0; width: 165px; padding: 5px; background: #fff; border: 1px solid #e0e3eb; border-radius: 9px; box-shadow: 0 10px 30px #0002; }
        .agp-notifications-filter-menu button { width: 100%; justify-content: flex-start; border: none; background: transparent; color: #25304b; }
        .agp-notifications-filter-menu button:hover, .agp-notifications-filter-menu button.selected { background: #f1f1ff; }
        .agp-notifications-list { display: grid; gap: 10px; }
        .agp-notification-card { display: flex; gap: 17px; padding: 22px; background: #fff; border: 1px solid #d8ddea; border-radius: 9px; min-width: 0; box-shadow: 0 1px 3px #182c4510; position: relative; overflow: hidden; }
        .agp-notification-card.is-unread:before { content: ""; position: absolute; top: 0; left: 0; bottom: 0; width: 3px; background: #292771; }
        .agp-notification-icon { width: 44px; height: 44px; flex: 0 0 44px; display: grid; place-items: center; border-radius: 11px; position: relative; }
        .agp-notification-unread-dot { position: absolute; width: 9px; height: 9px; bottom: 1px; right: 0; border-radius: 50%; background: #28256e; border: 1px solid white; }
        .agp-notification-body { flex: 1; min-width: 0; }
        .agp-notification-body h2 { margin: 0 0 3px; color: #1b2237; font-size: 16px; line-height: 1.35; font-weight: 700; overflow-wrap: anywhere; }
        .agp-notification-body time { display: block; color: #5c6170; font-size: 12px; font-weight: 600; }
        .agp-notification-body p { margin: 4px 0 0; color: #4c5365; font-size: 13px; line-height: 1.5; overflow-wrap: anywhere; }
        .agp-notification-priority { display: inline-block; background: #ffe0dc; color: #d6222b; border-radius: 999px; padding: 3px 11px; font-size: 11px; font-weight: 600; margin-top: 12px; }
        .agp-notification-actions { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 12px; }
        .agp-notification-actions button { border: 0; background: none; padding: 0; color: #30364c; font-size: 12px; cursor: pointer; }
        .agp-notification-actions button:hover { text-decoration: underline; }
        /* Primary View Detail button */
        .agp-notification-actions .agp-notification-detail {
          display: inline-flex; align-items: center; justify-content: center;
          min-height: 34px; padding: 0 16px; border: 1px solid #c51f28;
          border-radius: 7px; background: #d6222b; color: #fff;
          font-size: 12px; font-weight: 700; line-height: 1.2;
          box-shadow: 0 2px 5px rgba(214,34,43,.16);
          transition: background .18s ease, transform .18s ease, box-shadow .18s ease;
        }
        .agp-notification-actions .agp-notification-detail:hover {
          background: #b91c25; color: #fff; text-decoration: none;
          transform: translateY(-1px); box-shadow: 0 4px 10px rgba(214,34,43,.22);
        }
        .agp-notification-actions .agp-notification-detail:focus-visible {
          outline: 3px solid #f4a4a8; outline-offset: 2px;
        }
        .agp-notifications-empty { display: flex; justify-content: center; align-items: center; gap: 8px; padding: 60px 18px; border-radius: 10px; color: #7a8190; font-size: 14px; background: white; }
        @media (max-width: 600px) {
          .agp-notifications-page { min-height: calc(100dvh - 70px); padding: 15px 0 30px; }
          .agp-notifications-wrap { width: calc(100% - 16px); }
          .agp-notifications-heading h1 { font-size: 19px; }
          .agp-notifications-heading p { font-size: 12px; margin-top: 6px; }
          .agp-notifications-toolbar { gap: 10px; margin: 10px 0 12px; }
          .agp-notifications-toolbar button { font-size: 12px; min-height: 34px; padding: 0 10px; }
          .agp-notifications-list { gap: 8px; }
          .agp-notification-card { padding: 18px 12px 16px 18px; gap: 15px; border-radius: 7px; }
          .agp-notification-icon { width: 38px; height: 38px; flex-basis: 38px; border-radius: 10px; }
          .agp-notification-icon svg { width: 19px; height: 19px; }
          .agp-notification-body h2 { font-size: 13px; line-height: 1.25; }
          .agp-notification-body time { font-size: 10px; }
          .agp-notification-body p { font-size: 11.5px; line-height: 1.5; }
          .agp-notification-actions { gap: 12px; margin-top: 11px; }
          .agp-notification-actions button { font-size: 11px; }
          .agp-notification-actions .agp-notification-detail { min-height: 32px; padding: 0 12px; font-size: 11px; }
        }
      `}</style>
    </UserLayout>
  );
}
